/**
 * Single source of truth cho trạng thái "trỏ Page" khi build Product.
 *
 * ⚠️ ĐÂY CHỈ LÀ TRẠNG THÁI TRỎ PAGE — KHÔNG remove/comment code Page gốc.
 *    Mỗi điểm render chỉ thêm 1 điều kiện:
 *        isBuilt(path) ? <PageGốc/> : <ComingSoonPage/>
 *    Gỡ path khỏi danh sách dưới đây là Page gốc hiện lại nguyên vẹn, không cần restore.
 *
 * Cách dùng khi build Product:
 *   - Page đang dev-dở / chưa làm  → thêm vào PAGE_UNBUILD với status 'to-build'.
 *   - Page đã làm xong, đang QA     → để status 'to-test'.
 *   - Page đã test OK, mở thật      → XÓA dòng tương ứng (mặc định không nằm list = build).
 *
 * `match` chấp nhận:
 *   - Path đầy đủ:  '/report/inputs/exposure'
 *   - Prefix module: '/sales/control'  → che cả nhánh con ('/sales/control/...').
 */

export type UnbuildStatus =
  | 'to-build' // dev chưa làm / chưa build luôn
  | 'to-test' // đã làm xong, đang test để chuyển qua build Product

export interface UnbuildRule {
  match: string
  status: UnbuildStatus
}

/**
 * Sub-nav đang Coming Soon — liệt kê TỪNG sub-nav riêng (giống FACTORY_TAB_UNBUILD)
 * để bật/tắt độc lập từng cái. `match` vẫn nhận prefix nếu muốn che cả nhánh.
 */
export const PAGE_UNBUILD: UnbuildRule[] = [
  // CHỈ 2 function còn giữ trạng thái to-test (đang QA); mọi page/module KHÁC
  // (kể cả Master Data) không nằm trong list = build Production (default). Dùng
  // prefix match để che toàn bộ page con của từng function.
  { match: '/purchase/control', status: 'to-test' }, // Inbound Logistics
  { match: '/sales/control', status: 'to-test' }, //    Outbound Logistics
]

/**
 * Tab factory đang Coming Soon — match theo TAB KEY (áp cho mọi factory code:
 * baoloc / nhontrach / nguyencuong / nongsan / pacorini). Tab `summary` GIỮ gốc.
 *
 * Có thể match sâu hơn 1 cấp `tabKey/selectorValue` (vd hub `data`) để override
 * status cho 1 sub-page cụ thể — resolver ưu tiên key kết hợp trước, rồi tới tab key.
 */
export const FACTORY_TAB_DEFAULT_STATUS: UnbuildStatus = 'to-test'

export const FACTORY_TAB_UNBUILD: UnbuildRule[] = [
  // Tất cả tab factory = build/live. 3 card cũ (data/category-material,
  // data/movement-holidays, data/movement-kpis) không dùng nữa → gỡ khỏi list.
]

/**
 * Các status đang được "MỞ" — show Page gốc DÙ vẫn nằm trong danh sách Coming Soon.
 * Bật/tắt theo lệnh của chị Đẹp, KHÔNG xóa entry trong PAGE_UNBUILD / FACTORY_TAB_UNBUILD
 * (giữ nguyên log status để lật lại bất cứ lúc nào).
 *
 *   - 'to-test' đang ở đây  → mọi mục to-test show Page gốc.
 *   - Lệnh "chuyển show Page gốc cho status X"     → thêm X vào mảng này.
 *   - Lệnh "chuyển show Coming Soon cho status X"  → bỏ X khỏi mảng này.
 */
export const REVEALED_STATUSES: UnbuildStatus[] = ['to-test']

const isRevealed = (status: UnbuildStatus): boolean =>
  REVEALED_STATUSES.includes(status)

const hit = (path: string, m: string): boolean =>
  path === m || path.startsWith(`${m}/`)

/** Rule Coming Soon khớp với URL path (undefined nếu path là Page gốc / đang được mở). */
export const getUnbuild = (path: string): UnbuildRule | undefined => {
  const rule = PAGE_UNBUILD.find((r) => hit(path, r.match))
  return rule && !isRevealed(rule.status) ? rule : undefined
}

/** true = render Page gốc; false = render ComingSoonPage. */
export const isBuilt = (path: string): boolean => !getUnbuild(path)

/**
 * Rule Coming Soon khớp với tab factory (undefined nếu tab là Page gốc).
 * Ưu tiên key kết hợp `tabKey/selectorValue` (vd 'data/category-material')
 * trước khi fallback về tab key trần ('data').
 */
export const getFactoryTabUnbuild = (
  tabKey: string,
  selectorValue?: string,
): UnbuildRule | undefined => {
  let rule: UnbuildRule | undefined
  if (selectorValue) {
    rule = FACTORY_TAB_UNBUILD.find(
      (r) => r.match === `${tabKey}/${selectorValue}`,
    )
  }
  if (!rule) rule = FACTORY_TAB_UNBUILD.find((r) => r.match === tabKey)
  return rule && !isRevealed(rule.status) ? rule : undefined
}

/** true = render tab factory gốc; false = render ComingSoonPage. */
export const isFactoryTabBuilt = (
  tabKey: string,
  selectorValue?: string,
): boolean => !getFactoryTabUnbuild(tabKey, selectorValue)
